import { useState, useEffect } from 'react';
import { useClipboard } from '../../hooks/useClipboard';
import { useRaydium } from '../../hooks/useRaydium';
import {
    AddressLookupTableAccount,
    AddressLookupTableProgram,
    Connection,
    Keypair,
    LAMPORTS_PER_SOL,
    PublicKey,
    SystemProgram,
    TransactionInstruction,
    TransactionMessage,
    VersionedTransaction,
} from '@solana/web3.js';
import {
    createAssociatedTokenAccountIdempotentInstruction,
    createSyncNativeInstruction,
    getAccount,
    getAssociatedTokenAddress,
    getAssociatedTokenAddressSync,
    getMint,
    getOrCreateAssociatedTokenAccount,
} from '@solana/spl-token';
import { useSplToken } from '../../hooks/useSplToken';
import { WSOL } from '@raydium-io/raydium-sdk';
import { useJito } from '../../hooks/useJito';
import { deployPoolJitoTipDefault } from '../../../ConfigFees';

import '../../DeployPool.css';
import { usePumpswap } from '../../hooks/usePumpswap';
import { bs58 } from '@project-serum/anchor/dist/cjs/utils/bytes';

export function DeployPoolPumpswap({
    connection,
    wallet,
    deployedToken,
    buyers,
    bundleBuyValues,
    logMessage,
}: {
    connection: Connection;
    wallet: Keypair;
    deployedToken: string;
    buyers: Keypair[];
    bundleBuyValues: number[];
    logMessage: (message: string, type?: 'success' | 'error') => void;
}) {
    const { buildCreatePoolTx, getSellIx, getPoolId } = usePumpswap();
    const { getWalletTokenAccounts, getTokenAccountBalance, createTokenAccountsWithJito, transferAndWrapBatchWithJito } = useSplToken();
    const { getTipInstructions, sendBundle, spamAndConfirmBundle } = useJito();
    const { isCopied: isMarketIdCopied, copyToClipboard: copyMarketId } = useClipboard();
    const { isCopied: isBaseTokenCopied, copyToClipboard: copyBaseToken } = useClipboard();
    const { isCopied: isQuoteTokenCopied, copyToClipboard: copyQuoteToken } = useClipboard();

    const [baseTokenBalance, setBaseTokenBalance] = useState<number>(0);
    const [baseTokenDecimals, setBaseTokenDecimals] = useState<number>(0);
    const [baseTokenInitialLiquidity, setBaseTokenInitialLiquidity] = useState<number>(206900000);
    const [quoteTokenBalance, setQuoteTokenBalance] = useState<number>(0);
    const [quoteTokenDecimals, setQuoteTokenDecimals] = useState<number>(9);
    const [quoteTokenInitialLiquidity, setQuoteTokenInitialLiquidity] = useState<number>(84.990359266);

    const [advancedSettingsStatus, setAdvancedSettingsStatus] = useState<boolean>(true);
    const [advancedSettingsAddPurchaseStatus, setAdvancedSettingsAddPurchaseStatus] = useState<boolean>(true);

    const [advancedSettingsIsJitoTx, setAdvancedSettingsIsJitoTx] = useState<boolean>(true);
    const [advancedSettingsJitoTip, setAdvancedSettingsJitoTip] = useState<number>(deployPoolJitoTipDefault);

    useEffect(() => {
        const fetchTokenBalances = async () => {
            try {
                const baseTokenPublicKey = new PublicKey(deployedToken);

                const baseTokenAccount = await getAccount(connection, await getAssociatedTokenAddress(baseTokenPublicKey, wallet.publicKey), 'processed');
                const baseMint = await getMint(connection, baseTokenPublicKey, 'processed');
                const solBalance = await connection.getBalance(wallet.publicKey, 'processed');

                setBaseTokenDecimals(baseMint.decimals);
                setBaseTokenBalance(Number(baseTokenAccount.amount.toString()) / Math.pow(10, baseMint.decimals));
                setQuoteTokenBalance(solBalance / LAMPORTS_PER_SOL);
            } catch (error) {}
        };

        fetchTokenBalances();
    }, [deployedToken, connection, wallet]);

    const baseTokenInitialLiquidityChange = (event: { target: { value: string } }) => {
        setBaseTokenInitialLiquidity(Number(event.target.value));
    };

    const maxButtonForBaseToken = () => {
        setBaseTokenInitialLiquidity(baseTokenBalance);
    };

    const quoteTokenInitialLiquidityChange = (event: { target: { value: string } }) => {
        setQuoteTokenInitialLiquidity(Number(event.target.value));
    };

    const maxButtonForQuoteToken = () => {
        setQuoteTokenInitialLiquidity(quoteTokenBalance);
    };

    const advancedSettingsStatusChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setAdvancedSettingsStatus(checked);
    };

    const advancedSettingsAddPurchaseStatusChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setAdvancedSettingsAddPurchaseStatus(checked);
    };

    const advancedSettingsIsJitoTxChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setAdvancedSettingsIsJitoTx(checked);
    };

    const advancedSettingsJitoTipChange = (event: { target: { value: string } }) => {
        setAdvancedSettingsJitoTip(Number(event.target.value) * LAMPORTS_PER_SOL);
    };

    async function deployAmmPool() {
        try {
            logMessage('Deploying AMM pool...');
            const { poolAddress, deployIxs } = await buildCreatePoolTx(
                connection,
                deployedToken,
                baseTokenInitialLiquidity,
                baseTokenDecimals,
                quoteTokenInitialLiquidity,
                wallet.publicKey.toBase58(),
            );
            const tipIx = await getTipInstructions(wallet, advancedSettingsJitoTip);

            const message = new TransactionMessage({
                payerKey: wallet.publicKey,
                recentBlockhash: (await connection.getLatestBlockhash('finalized')).blockhash,
                instructions: [...deployIxs, tipIx],
            }).compileToV0Message();
            const versionedTx = new VersionedTransaction(message);
            versionedTx.sign([wallet]);
            const bundle: VersionedTransaction[] = [versionedTx];
            logMessage(`Deploying pool on: ${poolAddress}`);

            if (advancedSettingsAddPurchaseStatus) {
                //first 4 buyers
                const bundleBuyers = buyers.slice(0, 4);
                const buyValues = bundleBuyValues.slice(0, 4);

                const buyTxs = bundleBuyers.map(async (buyer, index) => {
                    const poolId = await getPoolId(wallet.publicKey, new PublicKey(WSOL.mint), new PublicKey(deployedToken));
                    const ix = getSellIx(
                        poolId,
                        new PublicKey(WSOL.mint),
                        new PublicKey(deployedToken),
                        buyer.publicKey,
                        BigInt(buyValues[index] * LAMPORTS_PER_SOL),
                        BigInt(0),
                    );

                    const tokenAta = getAssociatedTokenAddressSync(new PublicKey(deployedToken), buyer.publicKey);
                    const wsolAta = getAssociatedTokenAddressSync(new PublicKey(WSOL.mint), buyer.publicKey);
                    const accIxs = [
                        createAssociatedTokenAccountIdempotentInstruction(buyer.publicKey, wsolAta, buyer.publicKey, new PublicKey(WSOL.mint)),
                        SystemProgram.transfer({
                            fromPubkey: buyer.publicKey,
                            toPubkey: wsolAta,
                            lamports: BigInt(buyValues[index] * LAMPORTS_PER_SOL + 10001),
                        }),
                        createSyncNativeInstruction(wsolAta),
                        createAssociatedTokenAccountIdempotentInstruction(buyer.publicKey, tokenAta, buyer.publicKey, new PublicKey(deployedToken)),
                    ];

                    const tx = new VersionedTransaction(
                        new TransactionMessage({
                            payerKey: buyer.publicKey,
                            recentBlockhash: (await connection.getLatestBlockhash('finalized')).blockhash,
                            instructions: [...accIxs, ix],
                        }).compileToV0Message(),
                    );
                    tx.sign([buyer]);
                    return tx;
                });

                const resolvedBuyTxs = await Promise.all(buyTxs);

                bundle.push(...resolvedBuyTxs);
            }

            await spamAndConfirmBundle(connection, bundle, wallet, advancedSettingsJitoTip, true, 1000, 30000, true);

            logMessage(`Transaction sent: ${bs58.encode(versionedTx.signatures[0])}`, 'success');
        } catch (error) {
            console.error('Error deploying AMM pool:', error);
            logMessage(`Error deploying AMM pool: ${error}`, 'error');
        }
    }

    return (
        <section>
            <h2> Price & Initial Liquidity</h2>
            <section>
                <section className='deploy-pool-details'>
                    <div className='deploy-pool-row'>
                        <p>Base Token Mint Address:</p>
                        <div className='deploy-pool-row--right'>
                            <p>{deployedToken}</p>
                            <button className={isBaseTokenCopied ? 'copied-button' : 'copy-button'} onClick={() => copyBaseToken(deployedToken)}></button>
                        </div>
                    </div>
                    <div className='deploy-pool-row'>
                        <p>Quote Token Mint Address:</p>
                        <div className='deploy-pool-row--right'>
                            <p>{WSOL.mint}</p>
                            <button className={isQuoteTokenCopied ? 'copied-button' : 'copy-button'} onClick={() => copyQuoteToken(WSOL.mint)}></button>
                        </div>
                    </div>
                </section>
                <section className='initial-liquidity-section'>
                    <div className='initial-liquidity'>
                        <div className='initial-liquidity-row'>
                            <p>Base Token Initial Liquidity:</p>
                            <input type='number' value={baseTokenInitialLiquidity} onChange={baseTokenInitialLiquidityChange} />
                        </div>
                        <div className='initial-liquidity-row'>
                            <p>
                                Balance: <span>{baseTokenBalance}</span>
                            </p>
                            <button onClick={maxButtonForBaseToken}>MAX</button>
                        </div>
                    </div>
                    <div className='initial-liquidity'>
                        <div className='initial-liquidity-row'>
                            <p>Quote Token Initial Liquidity:</p>
                            <input type='number' value={quoteTokenInitialLiquidity} onChange={quoteTokenInitialLiquidityChange} />
                        </div>
                        <div className='initial-liquidity-row'>
                            <p>
                                Balance: <span>{quoteTokenBalance}</span>
                            </p>
                            <button onClick={maxButtonForQuoteToken}>MAX</button>
                        </div>
                    </div>
                </section>
            </section>
            <section className='advanced-settings-container'>
                <div className='advanced-settings-header'>
                    <h2>Advanced Settings</h2>
                    <label className='switch'>
                        <input type='checkbox' checked={advancedSettingsStatus} onChange={advancedSettingsStatusChange} />
                        <span className='slider round'></span>
                    </label>
                </div>
                <div>
                    {advancedSettingsStatus && (
                        <section className='advanced-settings-wrap'>
                            <div className='advanced-settings-option'>
                                <div className='advanced-settings-jito-container'>
                                    <div className='jito-tx'>
                                        <input type='checkbox' checked={advancedSettingsIsJitoTx} onChange={advancedSettingsIsJitoTxChange} />
                                        <p>JITO tx</p>
                                    </div>
                                    <div className='jito-tip'>
                                        <input
                                            type='number'
                                            value={advancedSettingsJitoTip / LAMPORTS_PER_SOL}
                                            onChange={advancedSettingsJitoTipChange}
                                            disabled={!advancedSettingsIsJitoTx}
                                        />
                                        <p>JITO tip SOL</p>
                                    </div>
                                </div>
                                {advancedSettingsIsJitoTx && (
                                    <div className='advanced-settings-add-purchase'>
                                        <input
                                            type='checkbox'
                                            checked={advancedSettingsAddPurchaseStatus}
                                            onChange={advancedSettingsAddPurchaseStatusChange}
                                            disabled={!advancedSettingsIsJitoTx || !bundleBuyValues.length}
                                        />
                                        <p>Add purchase inside of initial transaction (SOL)</p>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}
                </div>
            </section>
            <button className='button-accent button-250' onClick={() => deployAmmPool()}>
                Deploy Pool
            </button>
        </section>
    );
}
